import React, { useCallback } from 'react';
import { EdgeProps, getSmoothStepPath } from 'reactflow';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { actionsSelector } from '~/components/page/Automation/v2/state/actions';
import AnimatedPath from '../AnimatedPath';
import AddBlockButton from '../AddBlockButton';
import { getActionTemplate } from '../../../../constants/actionTemplates';
import useBuilderContext from '../../../../hooks/useBuilderContext';
import onUpdateActions from '../../utils/onUpdateActions';
import { FlowAction } from '~/graphql/types';
import { ClientFlowAction } from '~/graphql/types.client';
import interactions, {
  shouldDisable,
} from '~/components/page/Automation/v2/state/interactions';
import {
  DISABLED_EDGE_COLOR,
  MARKER_END_DISABLED,
} from '../../../MarkerDefinitions';
import { EDGE_BORDER_RADIUS } from '../BaseEdge';
import PasteContainer from '../PasteContainer';
import usePasteAction from '~/components/page/Automation/v2/components/Builder/hooks/usePasteAction';
import {
  type HandlerId,
  PathType,
} from '../../../nodeTypes/components/IfElseCard/types';
import dialogs from '~/components/page/Automation/v2/state/dialogs';

export enum Portal {
  Source = 'source',
  Target = 'target',
}

export type PortalEdgeData = {
  /** Source (parent) action id */
  sourceId: string;

  /** Target (child) action id */
  targetId: string;

  /** The edges that are shown only when cards are highlighted */
  isShownOnHighlight?: boolean;

  /** Color of portal edge */
  color: string;

  /** Type of portal */
  type?: Portal;
} | null;

export type Props = EdgeProps<PortalEdgeData> & {};

const PortalEdge: React.FCC<Props> = React.memo(
  ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    data,
    markerEnd,
    source,
    target,
    sourceHandleId,
  }) => {
    const { flowBlueprintId, accountId } = useBuilderContext();
    const edgePath = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: EDGE_BORDER_RADIUS,
    });

    const [actions, setActions] = useRecoilState(
      actionsSelector({
        accountId,
        flowBlueprintId,
      }),
    );

    const currentInteraction = useRecoilValue(interactions);
    const setDialog = useSetRecoilState(dialogs);
    const { onInsertAction } = usePasteAction({
      source,
      target,
      sourceKey: sourceHandleId as HandlerId,
    });

    const onAddAction = useCallback(
      (actionType: ClientFlowAction['actionType']) => {
        const actionTemplate = getActionTemplate({
          flowBlueprintId,
          accountId,
          actionType,
        });

        const targetAction = actions.find(a => a.id === data?.targetId);
        const primaryParentId =
          targetAction && 'parentIds' in targetAction
            ? targetAction.parentIds?.[0]
            : null;

        if (!primaryParentId) return;

        const resolvedSource =
          data?.type === 'source' ? data?.sourceId : primaryParentId;

        if (actionTemplate === null || data == null) return;

        if (actionType === FlowAction.IfElse) {
          setDialog({
            type: 'dialogIfElsePathSelection',
            mode: 'insert',
            action: actionTemplate,
            onConfirm: pathType => {
              setDialog(null);
              if (pathType === null) return;
              setActions(actions =>
                onUpdateActions(actions, {
                  source: resolvedSource,
                  target: data?.targetId,
                  subject: actionTemplate,
                  isPasteTruePath: pathType === PathType.True,
                }),
              );
            },
            onCancel: () => setDialog(null),
          });
          return;
        }

        setActions(actions =>
          onUpdateActions(actions, {
            source: resolvedSource,
            target: data?.targetId,
            subject: actionTemplate,
          }),
        );
      },
      [accountId, data, flowBlueprintId, setActions, actions, setDialog],
    );

    const disabled = useRecoilValue(shouldDisable);

    const actionsHighlighted = currentInteraction?.type === 'highlightActions';
    const connectionActive = currentInteraction?.type === 'connectAction';
    const copyActive = currentInteraction?.type === 'copyAction';
    const shownWhenHighlighted = data?.isShownOnHighlight;

    const selfHighlighted =
      actionsHighlighted &&
      shownWhenHighlighted &&
      currentInteraction?.highlightedActions?.[0] === data?.sourceId &&
      currentInteraction?.highlightedActions?.[1] === data?.targetId;

    const invisible = shownWhenHighlighted && !selfHighlighted;
    const edgeDisabled =
      (actionsHighlighted && !selfHighlighted) ||
      connectionActive ||
      copyActive;

    if (invisible) return null;

    return (
      <>
        <AnimatedPath
          id={id}
          style={{
            ...style,
            stroke: edgeDisabled ? DISABLED_EDGE_COLOR : data?.color,
            strokeWidth: selfHighlighted ? 3 : 2,
            pointerEvents: 'none',
          }}
          className="react-flow__edge-path"
          d={edgePath[0]}
          markerEnd={!edgeDisabled ? markerEnd : MARKER_END_DISABLED}
        />
        {data?.type === 'source' && (
          <PasteContainer
            onInsertAction={onInsertAction}
            sourceId={source}
            sourceKey={sourceHandleId as HandlerId}
            targetId={target}
            sourceX={sourceX}
            targetX={targetX}
            sourceY={sourceY}
            targetY={targetY}
          />
        )}
        {!disabled && (
          <AddBlockButton
            id={id}
            targetX={targetX}
            targetY={targetY}
            onAddAction={onAddAction}
          />
        )}
      </>
    );
  },
);

export default PortalEdge;
